import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ICommonVideoPlayer,
  MediaObjectFit,
} from '@epicuro-next/ataraxia/models';
import { setVideoAttributes } from '@epicuro-next/utilities-browser';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { VIDEO_ELEMENT_DEFAULT_ATTRS } from '../../models';

const SAFE_BUFFERING_TIME_DELAY = 200;

@Component({
  selector: 'atx-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VideoPlayerComponent
  implements OnChanges, OnInit, OnDestroy, ICommonVideoPlayer
{
  public pipActive = false;

  @Input()
  public url: string;

  @Input()
  public posterUrl: string;

  /**
   * Video element attributes to directly pass into element
   * Some values will be overwritten by input variables
   */
  @Input()
  public videoAttributes: Partial<HTMLVideoElement> = VIDEO_ELEMENT_DEFAULT_ATTRS;

  private playCommand$$ = new BehaviorSubject(false);

  @Input()
  public set play(play: boolean) {
    this.playCommand$$.next(play ?? this.videoAttributes.autoplay);
  }

  @Output()
  public videoReady: EventEmitter<void> = new EventEmitter();

  @Output()
  public videoError = new EventEmitter();

  /** Object fit strategy */
  @Input()
  public objectFit: MediaObjectFit = MediaObjectFit.CONTAIN;

  @ViewChild('video', { static: true })
  private videoElement: ElementRef<HTMLVideoElement>;

  @HostBinding('class')
  public get mediaObjectFit() {
    return this.pipActive ? MediaObjectFit.COVER : this.objectFit;
  }

  private destroy$$ = new Subject<void>();

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const urlChange: SimpleChange | null = changes.url;
    const videoAttributesChange: SimpleChange | null = changes.videoAttributes;

    if (urlChange && !urlChange.isFirstChange()) {
      this.videoElement.nativeElement.pause();
      this.videoElement.nativeElement.load();
      this.videoElement.nativeElement.play().catch(() => {});
    }

    if (videoAttributesChange && videoAttributesChange.isFirstChange()) {
      this.playCommand$$.next(videoAttributesChange.currentValue.autoplay);
    }

    setVideoAttributes({
      videoElement: this.videoElement.nativeElement,
      videoAttributes: this.videoAttributes,
    });
  }

  public ngOnInit() {
    if (this.videoAttributes.muted) {
      this.videoElement.nativeElement.volume = 0;
    }

    this.playCommand$$
      .pipe(debounceTime(SAFE_BUFFERING_TIME_DELAY), takeUntil(this.destroy$$))
      .subscribe((value) => this.videoController(value));
  }

  public onClickTmp(e: Event) {
    e.stopPropagation();
    e.preventDefault();
    this.videoController(this.videoElement.nativeElement.paused);
  }

  public requestPictureInPicture() {
    this.pipActive = true;
  }

  public exitPictureInPicture() {
    this.pipActive = false;
  }

  private videoController(play: boolean) {
    if (play) {
      this.videoElement.nativeElement.play();
      return;
    }

    this.videoElement.nativeElement.pause();
  }
}
